import { getApiClient, initApiClient } from '@/api/client';
import type { TokenGetter } from '@/api/middlewares/auth-middleware';
import { msalConfig } from '@/authConfig';
import { USER_PERMISSIONS_QUERY_KEY } from '@/pages/queries';
import { acquireIdToken } from '@/utils/acquire-id-token';
import { getRequiredEnv } from '@/utils/get-required-env';
import { useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type PropsWithChildren,
  type ReactElement,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export type User = {
  accountId: string;
  name: string;
  roles: string[];
  userId: string | null;
};

export type AuthContext = {
  isAuthenticated: boolean;
  isApiClientReady: boolean;
  isLoading: boolean;
  user: User | null;
  login: () => void;
  logout: () => void;
};

const AuthContextImpl = createContext<AuthContext>({
  isAuthenticated: false,
  isApiClientReady: false,
  isLoading: false,
  user: null,
  login: () => {},
  logout: () => {},
});

export const AuthProvider = ({ children }: PropsWithChildren): ReactElement => {
  const { instance, accounts, inProgress } = useMsal();
  const queryClient = useQueryClient();
  const isAuthenticated = accounts.length > 0;
  const [isApiClientReady, setIsApiClientReady] = useState(false);
  const isLoading = inProgress !== 'none';

  const account = instance.getActiveAccount() || accounts[0];

  const user: User | null = account
    ? {
        accountId: account.homeAccountId,
        name: account.name || '',
        roles: (account.idTokenClaims?.roles as string[]) || [],
        userId: (account?.localAccountId as string) ?? null,
      }
    : null;

  const hasSynced = useRef(false);
  useEffect(() => {
    if (!account || hasSynced.current) return;
    hasSynced.current = true;

    const getToken: TokenGetter = (options) =>
      acquireIdToken(
        instance,
        {
          scopes: ['openid', 'profile', 'offline_access'],
          account,
          authority: msalConfig.auth.authority,
        },
        options,
      );

    initApiClient(getToken).then(() => {
      setIsApiClientReady(true);
      getApiClient()
        .userMeSyncProfile()
        .catch((e) => {
          console.error('Failed to sync profile: ', e);
        });
    });
  }, [instance, account]);

  const login = () => {
    instance.loginRedirect({
      scopes: ['openid', 'profile', 'offline_access'],
      authority: msalConfig.auth.authority,
    });
  };

  const logout = () => {
    hasSynced.current = false;
    setIsApiClientReady(false);
    queryClient.removeQueries({ queryKey: USER_PERMISSIONS_QUERY_KEY });
    instance.logoutRedirect({
      postLogoutRedirectUri: getRequiredEnv('VITE_WEBAPP_BASE_URL'),
    });
  };

  const value: AuthContext = {
    isAuthenticated,
    isApiClientReady,
    isLoading,
    user,
    login,
    logout,
  };

  return (
    <AuthContextImpl.Provider value={value}>
      {children}
    </AuthContextImpl.Provider>
  );
};

export const useAuthContext = (): AuthContext => {
  const authContext = useContext(AuthContextImpl);

  if (!authContext) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return authContext;
};
